import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string" && value.length >= 10) {
    return value.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const buildFormState = (row = {}) => ({
  tenant_first_name: row?.tenant?.first_name || "",
  tenant_last_name: row?.tenant?.last_name || "",
  property_name: row?.property?.name || "",
  unit_name: row?.unit?.name || "",
  due_date: toDateInputValue(row?.billing?.due_date),
  landlord_name: row?.landlord?.name || "",
  payment_mode: row?.landlord?.payment_mode || "MOBILE_MONEY",
  mobile_number: row?.landlord?.mobile_number || "",
  account_number: row?.landlord?.account_number || "",
  account_name: row?.landlord?.account_name || "",
  bank_name: row?.landlord?.bank_name || "",
  account_branch: row?.landlord?.account_branch || "",
  sort_code: row?.landlord?.sort_code || "",
});

export default function EditAnonymousTenancy({
  open,
  row,
  onClose,
  onSave,
  isSaving,
  errorMessage,
}) {
  const [form, setForm] = useState(buildFormState(row));

  useEffect(() => {
    if (open) {
      setForm(buildFormState(row));
    }
  }, [open, row]);

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave?.(form);
  };

  const isBank = form.payment_mode === "BANK";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Edit Anonymous Tenancy</DialogTitle>
        <DialogContent>
          <Box display="grid" gap={2} mt={1}>
            <TextField
              label="Tenant first name"
              value={form.tenant_first_name}
              onChange={handleChange("tenant_first_name")}
              fullWidth
            />
            <TextField
              label="Tenant last name"
              value={form.tenant_last_name}
              onChange={handleChange("tenant_last_name")}
              fullWidth
            />
            <TextField
              label="Property name"
              value={form.property_name}
              onChange={handleChange("property_name")}
              fullWidth
            />
            <TextField
              label="Unit name"
              value={form.unit_name}
              onChange={handleChange("unit_name")}
              fullWidth
            />
            <TextField
              label="Due date"
              type="date"
              value={form.due_date}
              onChange={handleChange("due_date")}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Landlord / payout name"
              value={form.landlord_name}
              onChange={handleChange("landlord_name")}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Payment mode</InputLabel>
              <Select
                label="Payment mode"
                value={form.payment_mode}
                onChange={handleChange("payment_mode")}
              >
                <MenuItem value="MOBILE_MONEY">Mobile Money</MenuItem>
                <MenuItem value="BANK">Bank</MenuItem>
              </Select>
            </FormControl>
            {isBank ? (
              <>
                <TextField
                  label="Account name"
                  value={form.account_name}
                  onChange={handleChange("account_name")}
                  fullWidth
                />
                <TextField
                  label="Bank name"
                  value={form.bank_name}
                  onChange={handleChange("bank_name")}
                  fullWidth
                />
                <TextField
                  label="Account number"
                  value={form.account_number}
                  onChange={handleChange("account_number")}
                  fullWidth
                />
                <TextField
                  label="Branch"
                  value={form.account_branch}
                  onChange={handleChange("account_branch")}
                  fullWidth
                />
                <TextField
                  label="Sort code"
                  value={form.sort_code}
                  onChange={handleChange("sort_code")}
                  fullWidth
                />
              </>
            ) : (
              <TextField
                label="Mobile number"
                value={form.mobile_number}
                onChange={handleChange("mobile_number")}
                fullWidth
              />
            )}
            {errorMessage ? (
              <Box color="error.main" fontSize={14}>
                {errorMessage}
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSaving}>
            Save changes
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
